import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { catchError, map, Observable, of, throwError } from 'rxjs';

import { environment } from '../../../environments/environment';
import {
  CreateUserRequest,
  CreateUserResponse,
  DuplicatePersonResponse,
  GetUserResponse,
  UpdateUserRequest,
  ListUsersResponse,
  UserListItem,
} from '../../shared/interfaces/user.interface';
import { skipLoading } from '../interceptors/skip-loading.context';
import { PerfilAsistenciaResponse } from '../../shared/helpers/perfil-asistencia';
import { ReunionCounts } from '../../shared/helpers/grafico-pie';
import { UserStore } from './user-store.service';

@Injectable({ providedIn: 'root' })
export class UsersService {
  private readonly http = inject(HttpClient);
  private readonly userStore = inject(UserStore);
  private readonly apiUrl = environment.apiUrl;

  /** Igual que Mayorap2: avisa solo si nombre + apellido ya están cargados. */
  buscarDuplicado(nombre: string, apellido: string): Observable<DuplicatePersonResponse> {
    return this.http.get<DuplicatePersonResponse>(`${this.apiUrl}/users/duplicado`, {
      params: { nombre, apellido },
      context: skipLoading(),
    });
  }

  getById(id: string): Observable<GetUserResponse> {
    return this.http.get<GetUserResponse>(`${this.apiUrl}/users/${id}`);
  }

  /**
   * Últimas 5 semanas. Path igual a mayorap2:
   * fechaDom0..4 (consul1..5, más reciente primero), iglesia, id.
   */
  getPerfilAsistencia(
    fechasIso: readonly string[],
    iglesia: string,
    id: string,
  ): Observable<PerfilAsistenciaResponse> {
    const path = [...fechasIso, iglesia, id].join(',');
    return this.http.get<PerfilAsistenciaResponse>(`${this.apiUrl}/getPerfilAsistencia/${path}`);
  }

  getCurrentUser(): Observable<GetUserResponse> {
    const id = this.userStore.user()?._id;
    if (!id) {
      return throwError(() => new Error('No hay usuario en sesión'));
    }

    return this.getById(id);
  }

  list(params?: {
    page?: number;
    limit?: number;
    sort?: string;
    filtro?: string;
    q?: string;
  }): Observable<ListUsersResponse> {
    const httpParams: Record<string, string> = {
      page: String(params?.page ?? 1),
      limit: String(params?.limit ?? 100),
      sort: params?.sort ?? 'nombre:asc',
    };
    if (params?.filtro) {
      httpParams['filtro'] = params.filtro;
    }
    if (params?.q) {
      httpParams['q'] = params.q;
    }
    return this.http.get<ListUsersResponse>(`${this.apiUrl}/users`, {
      params: httpParams,
    });
  }

  getCantMujeres(iglesia: string): Observable<{ totalMujeres: number }> {
    return this.http.get<{ totalMujeres: number }>(this.conteo('getCantMujeres', iglesia));
  }

  getCantHombres(iglesia: string): Observable<{ totalHombres: number }> {
    return this.http.get<{ totalHombres: number }>(this.conteo('getCantHombres', iglesia));
  }

  getBautismo(iglesia: string): Observable<[number, number]> {
    return this.http.get<[number, number]>(this.conteo('getBautismo', iglesia));
  }

  getEnCelula(iglesia: string): Observable<{ encelu: number }> {
    return this.http.get<{ encelu: number }>(this.conteo('getEncelu', iglesia));
  }

  getSinCelula(iglesia: string): Observable<{ sincelu: number }> {
    return this.http.get<{ sincelu: number }>(this.conteo('getSincelu', iglesia));
  }

  getCantLideres(iglesia: string): Observable<{ cantLideres: number }> {
    return this.http.get<{ cantLideres: number }>(this.conteo('getCantLideres', iglesia));
  }

  getCantAdmin(iglesia: string): Observable<{ cantAdmin: number }> {
    return this.http.get<{ cantAdmin: number }>(this.conteo('getCantAdmin', iglesia));
  }

  getCantNiveles(iglesia: string): Observable<Record<string, number>> {
    return this.http.get<Record<string, number>>(this.conteo('getCantNiveles', iglesia));
  }

  getEdades(iglesia: string): Observable<number[]> {
    return this.http.get<number[]>(this.conteo('getEdades', iglesia));
  }

  getReunionSelect(iglesia: string): Observable<ReunionCounts> {
    return this.http.get<ReunionCounts>(this.conteo('getReunionSelect', iglesia));
  }

  getConteoDom(fechas: string[], iglesia: string): Observable<Record<string, number>> {
    return this.http.get<Record<string, number>>(this.conteoFechas('conteoDomNew', fechas, iglesia));
  }

  getConteoFull(fechas: string[], iglesia: string): Observable<{ contador: number }[]> {
    return this.http.get<{ contador: number }[]>(this.conteoFechas('conteoFullNew', fechas, iglesia));
  }

  getConteoCelTrue(fechas: string[], iglesia: string): Observable<Record<string, number>> {
    return this.http.get<Record<string, number>>(this.conteoFechas('conteoCelTrue', fechas, iglesia));
  }

  getConteoCelFalse(fechas: string[], iglesia: string): Observable<Record<string, number>> {
    return this.http.get<Record<string, number>>(this.conteoFechas('conteoCelFalse', fechas, iglesia));
  }

  private conteo(ruta: string, iglesia: string): string {
    return `${this.apiUrl}/${ruta}/${encodeURIComponent(iglesia)}`;
  }

  private conteoFechas(ruta: string, fechas: string[], iglesia: string): string {
    return `${this.apiUrl}/${ruta}/${fechas.join(',')},${encodeURIComponent(iglesia)}`;
  }

  /** Todos los líderes activos de la iglesia de la sesión. */
  getLideres(): Observable<UserListItem[]> {
    return this.http
      .get<{ success: boolean; data: UserListItem[] }>(`${this.apiUrl}/users/lideres`)
      .pipe(
        map((response) => response.data ?? []),
        catchError(() => of([])),
      );
  }

  create(payload: CreateUserRequest): Observable<CreateUserResponse> {
    return this.http.post<CreateUserResponse>(`${this.apiUrl}/users`, payload);
  }

  update(id: string, payload: UpdateUserRequest): Observable<GetUserResponse> {
    return this.http.put<GetUserResponse>(`${this.apiUrl}/users/${id}`, payload);
  }

  /** Discípulos directos de un líder. Solo administradores. */
  listDiscipulos(idLider: string): Observable<UserListItem[]> {
    return this.http
      .get<{ success: boolean; data: UserListItem[] }>(`${this.apiUrl}/users/${idLider}/discipulos`)
      .pipe(map((response) => response.data ?? []));
  }

  /** Pasa discípulos elegidos de un líder a otro. Solo administradores. */
  transferirDiscipulos(
    idLider: string,
    ids: string[],
    idLiderDestino: string,
  ): Observable<{ transferidos: number }> {
    return this.http
      .post<{ success: boolean; data: { transferidos: number } }>(
        `${this.apiUrl}/users/${idLider}/transferir-discipulos`,
        { ids, idLiderDestino },
      )
      .pipe(map((response) => response.data));
  }

  marcarDiscipulado(
    id: string,
    accion: 'estudio-terminado' | 'cancelar-estudio' | 'bautismo-realizado' | 'cancelar-bautismo',
  ): Observable<{ success: boolean; data: UserListItem }> {
    return this.http.post<{ success: boolean; data: UserListItem }>(
      `${this.apiUrl}/users/${id}/discipulado`,
      { accion },
    );
  }

  reiniciarFaltas(id: string): Observable<{ success: boolean; data: UserListItem }> {
    return this.http.post<{ success: boolean; data: UserListItem }>(
      `${this.apiUrl}/users/${id}/reiniciar-faltas`,
      {},
    );
  }

  listPapelera(params?: {
    page?: number;
    limit?: number;
    q?: string;
  }): Observable<ListUsersResponse> {
    const httpParams: Record<string, string> = {
      page: String(params?.page ?? 1),
      limit: String(params?.limit ?? 12),
    };
    if (params?.q) {
      httpParams['q'] = params.q;
    }
    return this.http.get<ListUsersResponse>(`${this.apiUrl}/users/papelera`, {
      params: httpParams,
    });
  }

  restore(id: string): Observable<{ success: boolean; data: UserListItem }> {
    return this.http.put<{ success: boolean; data: UserListItem }>(
      `${this.apiUrl}/users/${id}/restaurar`,
      {},
    );
  }

  delete(
    id: string,
    perma = false,
    discipulos?: { accion: 'transferir' | 'sin-lider'; idLiderDestino?: string },
  ): Observable<{ success: boolean; data: { id: string; message: string } }> {
    const params: Record<string, string> = { perma: String(perma) };
    if (discipulos?.accion) {
      params['discipulos'] = discipulos.accion;
    }
    if (discipulos?.idLiderDestino) {
      params['idLiderDestino'] = discipulos.idLiderDestino;
    }
    return this.http.delete<{ success: boolean; data: { id: string; message: string } }>(
      `${this.apiUrl}/users/${id}`,
      { params },
    );
  }
}